import { ExternalLinkIcon } from 'lucide-react';
import { Pill } from '@bcecloud/portal-ui';
import { eventSummary, type PanelLead } from '@/lib/lead-panel';

export interface LeadCellProps {
  readonly lead: PanelLead;
}

/** A lead's who: company over contact and email, with any spam flags. */
export function LeadCell({ lead }: LeadCellProps): React.ReactElement {
  return (
    <>
      <span className="block font-bold text-portal-ink">{lead.company || lead.contactName || 'No name'}</span>
      <span className="block text-portal-micro text-portal-muted">
        {[lead.company ? lead.contactName : null, lead.email].filter(Boolean).join(' · ') || 'No contact details'}
      </span>
      {lead.spamFlags.length > 0 ? (
        <span className="mt-1 flex flex-wrap gap-1">
          {lead.spamFlags.map((flag) => (
            <Pill key={flag.flag} tone="warning">
              Likely spam: {flag.label}
            </Pill>
          ))}
        </span>
      ) : null}
    </>
  );
}

export interface EventCellProps {
  readonly lead: PanelLead;
}

/** A lead's what: the event summary over the first program choice. */
export function EventCell({ lead }: EventCellProps): React.ReactElement {
  const summary = eventSummary(lead);
  return (
    <>
      <span className={summary ? 'block text-portal-ink' : 'block text-portal-muted'}>{summary || 'No event details'}</span>
      {lead.program ? <span className="block text-portal-micro text-portal-muted">{lead.program}</span> : null}
    </>
  );
}

export interface SalesforceLinkProps {
  readonly lead: PanelLead;
}

/** Opens the lead in Salesforce in a new tab. */
export function SalesforceLink({ lead }: SalesforceLinkProps): React.ReactElement {
  return (
    <a
      href={lead.salesforceUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center text-brand"
      aria-label={`Open ${lead.company || lead.contactName || 'the lead'} in Salesforce`}
    >
      <ExternalLinkIcon className="size-4" aria-hidden="true" />
    </a>
  );
}
